// HM-332/333 cluster 16: client API per pricing-rules + holidays + room-types.
// Coperture: list/get/create/update/delete + duplicate + preview-calc.

import { api } from './client.js';

export type PricingRule = {
  id: string;
  name: string;
  base2hPrice: string;
  hour3Supplement: string;
  hour4plusUnit: string;
  overnightPrice: string;
  weekendMaxHours: number | null;
  weekendSurchargePct: number;
  holidayMaxHours: number | null;
  holidaySurchargePct: number;
  bufferMinutes: number;
  roundingMode: 'full_hour' | 'half_hour';
  overrideRole: 'admin_only' | 'admin_reception';
  active: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CustomTier = {
  id: string;
  pricingRuleId: string;
  hourFrom: number;
  unitPrice: string;
  displayOrder: number;
};

export type Holiday = {
  id: string;
  date: string;
  label: string;
  recurring: boolean;
  active: boolean;
  createdAt: string;
};

export type RoomTypeFull = {
  id: string;
  tenantId: string;
  code: 'COM' | 'STD' | 'DLX' | 'SUI';
  label: string;
  description: string | null;
  basePriceDayUse: string;
  extraHourPrice: string;
  basePriceOvernight: string;
  pricingRuleId: string | null;
  displayOrder: number;
  active: boolean;
};

export type PriceCalcResult = {
  total: number;
  breakdown: { label: string; amount: number }[];
  appliedCap: 'none' | 'overnight' | 'weekend' | 'holiday';
  isWeekend: boolean;
  isHoliday: boolean;
};

// ---------- pricing-rules ----------

export async function listPricingRules(): Promise<{ pricingRules: PricingRule[] }> {
  return api('/pricing-rules');
}

export async function getPricingRule(
  id: string,
): Promise<{ pricingRule: PricingRule; customTiers: CustomTier[] }> {
  return api(`/pricing-rules/${id}`);
}

export async function createPricingRule(
  body: Omit<PricingRule, 'id' | 'active' | 'createdAt' | 'updatedAt'> & {
    base2hPrice: number;
    hour3Supplement: number;
    hour4plusUnit: number;
    overnightPrice: number;
  },
): Promise<{ pricingRule: PricingRule }> {
  return api('/pricing-rules', { method: 'POST', body: JSON.stringify(body) });
}

export async function updatePricingRule(
  id: string,
  patch: Partial<{
    name: string;
    base2hPrice: number;
    hour3Supplement: number;
    hour4plusUnit: number;
    overnightPrice: number;
    weekendMaxHours: number | null;
    weekendSurchargePct: number;
    holidayMaxHours: number | null;
    holidaySurchargePct: number;
    bufferMinutes: number;
    roundingMode: 'full_hour' | 'half_hour';
    overrideRole: 'admin_only' | 'admin_reception';
    active: boolean;
  }>,
): Promise<{ pricingRule: PricingRule }> {
  return api(`/pricing-rules/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function duplicatePricingRule(id: string): Promise<{ pricingRule: PricingRule }> {
  return api(`/pricing-rules/${id}/duplicate`, { method: 'POST' });
}

export async function deletePricingRule(id: string): Promise<void> {
  await api(`/pricing-rules/${id}`, { method: 'DELETE' });
}

export async function previewCalc(input: {
  pricingRuleId?: string;
  rule?: Record<string, unknown>;
  hours: number;
  date: string;
}): Promise<PriceCalcResult> {
  return api('/pricing-rules/preview-calc', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// ---------- holidays ----------

export async function listHolidays(year?: string): Promise<{ holidays: Holiday[] }> {
  const q = year ? `?year=${year}` : '';
  return api(`/holidays${q}`);
}

export async function createHoliday(body: {
  date: string;
  label: string;
  recurring?: boolean;
}): Promise<{ holiday: Holiday }> {
  return api('/holidays', { method: 'POST', body: JSON.stringify(body) });
}

export async function deleteHoliday(id: string): Promise<void> {
  await api(`/holidays/${id}`, { method: 'DELETE' });
}

// ---------- room pricing context (HM-337 cluster 16.1) ----------

export type RoomPricingContext = {
  room: {
    id: string;
    code: string;
    floor: string;
    building: string;
    status: string;
    roomTypeId: string;
  };
  type: {
    id: string;
    code: string;
    label: string;
    description: string | null;
    pricingRuleId: string | null;
  };
  pricingRule: PricingRule | null;
};

export async function fetchRoomPricingContext(roomId: string): Promise<RoomPricingContext> {
  return api(`/rooms/${roomId}/pricing-context`);
}

// ---------- room-types extended ----------

export async function listRoomTypesFull(): Promise<{ roomTypes: RoomTypeFull[] }> {
  return api('/room-types');
}

export async function updateRoomType(
  id: string,
  patch: Partial<{ label: string; pricingRuleId: string | null; displayOrder: number }>,
): Promise<{ roomType: RoomTypeFull }> {
  return api(`/room-types/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}
